import { FC, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContentById, updateContent, Content, VideoData } from '@/services/university-service';
import {
  Spinner,
  Center,
  VStack,
  useToast,
  Button,
  HStack,
  Text,
  Icon,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  Image
} from '@chakra-ui/react';
import { MdSave, MdArrowBack } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES } from '@bemap/utils';
import { useForm } from 'react-hook-form';
import { Panel, PageHeading } from '@bemap/ui';

interface VideoContentFormData {
  title: string;
  videoUrl: string;
  order: number;
  duration: number;
}

const EditVideoContentPage: FC = () => {
  const { courseId, contentId } = useParams<{ courseId: string; moduleId: string; contentId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState<Content | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid }
  } = useForm<VideoContentFormData>({
    defaultValues: {
      title: '',
      videoUrl: '',
      order: 1,
      duration: 0
    }
  });

  // Load content data
  useEffect(() => {
    const loadContent = async () => {
      if (!contentId) return;

      try {
        setIsLoading(true);
        const response = await getContentById(parseInt(contentId));

        if (response.statusCode === 200 && response.message) {
          const contentData = response.message as Content;
          setContent(contentData);

          // Extract video data from content.data
          const videoData = contentData.data as VideoData;

          // Populate form with content data
          reset({
            title: contentData.title,
            videoUrl: videoData?.videoUrl || '',
            order: contentData.order,
            duration: contentData.duration ? parseInt(contentData.duration) : 0
          });

          // Set thumbnail if available
          if (videoData?.thumbnailUrl) {
            setThumbnailUrl(videoData.thumbnailUrl);
          }
        } else {
          throw new Error(response.message || t('university.content.content_not_found'));
        }
      } catch (error: any) {
        console.error('Error loading content:', error);
        toast({
          title: t('university.content.error_loading_content'),
          description: error.message || t('general.error'),
          status: 'error',
          duration: 30,
          isClosable: true,
        });
        navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [contentId, navigate, toast, reset, t, courseId]);

  const handleBack = () => {
    navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
  };

  const onSubmit = async (data: VideoContentFormData) => {
    if (!contentId) return;

    setIsSubmitting(true);

    try {
      const videoData: VideoData = {
        videoUrl: data.videoUrl,
        thumbnailUrl: thumbnailUrl || undefined,
        duration: data.duration
      };

      const contentData: Partial<Content> = {
        title: data.title,
        data: videoData,
        duration: data.duration.toString()
      };

      const response = await updateContent(parseInt(contentId), contentData);

      if (response.statusCode === 200) {
        toast({
          title: t('university.content.video_updated'),
          description: `${t('actions.edit')} "${data.title}" ${t('university.content.video_updated')}`,
          status: 'success',
          duration: 30,
          isClosable: true,
        });

        navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
      } else {
        throw new Error(response.message || t('university.content.error_updating_video'));
      }
    } catch (error: any) {
      console.error('Error updating video content:', error);
      toast({
        title: t('university.content.error_updating_video'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 30,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVideoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setValue('videoUrl', url);

    // Try to extract thumbnail from YouTube URL
    if (url.includes('youtube.com/watch?v=') || url.includes('youtu.be/')) {
      const videoId = url.includes('youtube.com/watch?v=')
        ? url.split('v=')[1].split('&')[0]
        : url.split('youtu.be/')[1]?.split('?')[0];

      if (videoId) {
        setThumbnailUrl(`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`);
      }
    } else if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('/')[0];
      if (videoId) {
        // Vimeo thumbnail would require API call, using placeholder for now
        setThumbnailUrl('https://via.placeholder.com/480x360/cccccc/666666?text=Vimeo+Video');
      }
    } else {
      setThumbnailUrl('');
    }
  };

  const getVideoEmbedUrl = (url: string) => {
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    } else if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('/')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : '';
    }
    return '';
  };

  if (isLoading) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>{t('general.loading')}</Text>
        </VStack>
      </Center>
    );
  }

  if (!content) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Text>{t('university.content.content_not_found')}</Text>
          <Button onClick={handleBack}>{t('university.content.back_to_course')}</Button>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading title={`${t('actions.edit')}: ${content.title}`} actions={[
        {
          label: t('university.content.back_to_course'),
          onClick: handleBack,
          icon: <Icon as={MdArrowBack} />,
          variant: 'outline'
        }
      ]} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack align="stretch" spacing={6}>
          {/* Video Content Form */}
          <Panel heading={t('university.content.video_information')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!errors.title} isRequired>
                <FormLabel>{t('university.content.title')}</FormLabel>
                <Input
                  {...register('title', {
                    required: t('university.validation.title_required'),
                    minLength: { value: 3, message: t('university.validation.title_min_length') }
                  })}
                  placeholder={t('university.placeholders.video_title')}
                />
                <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.videoUrl} isRequired>
                <FormLabel>{t('university.content.video_url')}</FormLabel>
                <Input
                  {...register('videoUrl', {
                    required: t('university.validation.video_url_required'),
                    pattern: {
                      value: /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|vimeo\.com\/)/,
                      message: t('university.validation.video_url_format')
                    }
                  })}
                  placeholder={t('university.placeholders.video_url')}
                  onChange={handleVideoUrlChange}
                />
                <FormErrorMessage>{errors.videoUrl?.message}</FormErrorMessage>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  {t('university.content.supported_platforms')}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>{t('university.content.video_duration')}</FormLabel>
                <NumberInput min={1} defaultValue={0}>
                  <NumberInputField {...register('duration', { valueAsNumber: true })} />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text fontSize="sm" color="gray.600" mt={1}>
                  {t('university.content.minutes')}
                </Text>
              </FormControl>
            </VStack>
          </Panel>

          {/* Video Preview */}
          {watch('videoUrl') && (
            <Panel heading={t('university.content.video_preview')} padding={6}>
              <VStack spacing={4} align="stretch">
                {thumbnailUrl && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      {t('university.content.thumbnail')}
                    </Text>
                    <Image
                      src={thumbnailUrl}
                      alt="Video thumbnail"
                      maxH="200px"
                      borderRadius="md"
                      objectFit="cover"
                    />
                  </Box>
                )}

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    {t('university.content.embed_preview')}
                  </Text>
                  <Box
                    position="relative"
                    paddingBottom="56.25%"
                    borderRadius="md"
                    overflow="hidden"
                    bg="gray.100"
                  >
                    <iframe
                      src={getVideoEmbedUrl(watch('videoUrl'))}
                      title="Video preview"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 0
                      }}
                      allowFullScreen
                    />
                  </Box>
                </Box>
              </VStack>
            </Panel>
          )}

          {/* Action Buttons */}
          <HStack justify="space-between">
            <Button
              variant="outline"
              onClick={handleBack}
              isDisabled={isSubmitting}
            >
              {t('actions.cancel')}
            </Button>

            <HStack spacing={3}>
              <Button
                type="submit"
                colorScheme="blue"
                leftIcon={<Icon as={MdSave} />}
                isLoading={isSubmitting}
                loadingText={t('general.saving')}
                isDisabled={!isValid}
              >
                {t('actions.save')}
              </Button>
            </HStack>
          </HStack>
        </VStack>
      </form>
    </VStack>
  );
};

export default EditVideoContentPage; 